// Роль-слоты кадров промпта — отдельный concern поверх очереди
// вложений: раскладка pending по ролям режима, авто-перевод плоского пула в
// слоты и загрузка в конкретный слот. Живёт рядом с `useChatAttachments`, но
// отдельным хуком: у пула вложений своя ответственность (kind-лимиты, политика
// типов, picker-стейт), и смешивание двух режимов работы в одном хуке быстро
// растит его сложность.

import { useCallback, useEffect, useMemo } from "react";
import toast from "react-hot-toast";

import { useDraftChatStore } from "~/shared/store/useDraftChatStore";

import { hasRequiredRoles, itemsByRole, planRoleAssignment } from "./role-slot-helpers";

import type { TFunction } from "i18next";
import type { ImageInputRole, ModeConfig } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

export interface UseImageRoleSlotsParams {
    chatId: string | null;
    currentMode: ModeConfig | null;
    items: readonly PendingUpload[];
    /** `mode.minInputImages` — сколько первых ролей обязательно. */
    minImages: number;
    /** Загрузка в слот идёт тем же путём, что и пул, но с готовой ролью в seed. */
    uploadToRole: (role: ImageInputRole, files: File[]) => Promise<unknown>;
    t: TFunction<"chat">;
}

export function useImageRoleSlots({
    chatId,
    currentMode,
    items,
    minImages,
    uploadToRole,
    t,
}: UseImageRoleSlotsParams) {
    const updatePending = useDraftChatStore((s) => s.updatePending);
    const removePending = useDraftChatStore((s) => s.removePending);

    const imageRoles = useMemo(() => currentMode?.imageRoles ?? [], [currentMode]);
    const hasImageRoles = imageRoles.length > 0;
    const itemByRole = useMemo(() => itemsByRole(items, imageRoles), [items, imageRoles]);
    const requiredRoleCount = Math.min(minImages, imageRoles.length);

    // У картинки без роли нет слота — она не видна в UI и не уедет в payload.
    // Раскладываем такие по свободным ролям в порядке пула (его задавал
    // пользователь перетаскиванием), лишние убираем. Эффект идемпотентен: как
    // только безролевых не осталось, план пуст и он не зацикливается — поэтому
    // он ловит и смену режима, и уже накопленный пул чата, открытого сразу в
    // ролевом режиме.
    useEffect(() => {
        if (!chatId || !hasImageRoles) return;
        const plan = planRoleAssignment(items, imageRoles);
        if (plan.assign.length === 0 && plan.remove.length === 0) return;
        for (const { localId, role } of plan.assign) updatePending(chatId, localId, { role });
        for (const item of plan.remove) removePending(chatId, item.localId);
        if (plan.remove.length > 0) {
            toast(t("prompt.attachmentsRemoved", { count: plan.remove.length }), { icon: "ℹ️" });
        }
    }, [items, chatId, hasImageRoles, imageRoles, updatePending, removePending, t]);

    // Слот держит одну картинку — лишние выбранные файлы отбрасываем здесь, а не
    // в компоненте, чтобы правило действовало на любом пути (файл / пикер).
    const handlePickFilesForRole = useCallback(
        async (role: ImageInputRole, files: File[]) => {
            if (!chatId) return;
            await uploadToRole(role, files.slice(0, 1));
        },
        [chatId, uploadToRole],
    );

    return {
        imageRoles,
        hasImageRoles,
        itemByRole,
        requiredRoleCount,
        hasRequiredRoleFiles: hasRequiredRoles(itemByRole, imageRoles, requiredRoleCount),
        handlePickFilesForRole,
    };
}
